import { EFFECT_DURATION_MS, SHROOM_DODGE_CHANCE, SNOWSHROOM_COOLDOWN } from './effects';

/**
 * The Fungi Forest's two items (spec
 * docs/superpowers/specs/2026-10-24-fungi-forest-level-7-and-items-design.md).
 * Both are used from Storage on ONE tower type, like the targeted potions, and
 * run for 3:00 of wall-clock time. Neither is for sale: they drop on Fungi
 * Forest wins (Normal track only), each roll on its own `chestRandom()`
 * number — never the sim's seeded Rng.
 *
 * - The Shroom: 25% on every win of Mushy Mayhem and every Fungi level after it.
 * - The Snowshroom: 25% on every win of Icy Photosynthesis, and only there.
 */

export type FungiItemKind = 'shroom' | 'snowshroom';

export const FUNGI_ITEM_KINDS: readonly FungiItemKind[] = ['shroom', 'snowshroom'];

export interface FungiItemDef {
  kind: FungiItemKind;
  name: string;
  /** One line for the dialogs and the tower picker. */
  effect: string;
  durationMs: number;
  /** Storage / claim art (96px). */
  art: string;
  /** The synced count key. */
  storageKey: string;
}

export const FUNGI_ITEMS: Record<FungiItemKind, FungiItemDef> = {
  shroom: {
    kind: 'shroom',
    name: 'The Shroom',
    effect: `One tower dodges ${Math.round(SHROOM_DODGE_CHANCE * 100)}% of the attacks thrown at it`,
    durationMs: EFFECT_DURATION_MS.shroom,
    art: 'item-shroom',
    storageKey: 'hambi.shroom',
  },
  snowshroom: {
    kind: 'snowshroom',
    name: 'Snowshroom',
    effect: `One tower SHROOMNITIZES an enemy every ${SNOWSHROOM_COOLDOWN} s: it hits another enemy once`,
    durationMs: EFFECT_DURATION_MS.snowshroom,
    art: 'item-snowshroom',
    storageKey: 'hambi.snowshroom',
  },
};

export function isFungiItemKind(v: unknown): v is FungiItemKind {
  return typeof v === 'string' && (FUNGI_ITEM_KINDS as readonly string[]).includes(v);
}

/** The chance each drop roll pays. */
export const FUNGI_ITEM_DROP_CHANCE = 0.25;
/** Mushy Mayhem and every Fungi Forest level after it drop the Shroom. */
export const SHROOM_DROP_LEVEL_IDS: readonly number[] = [115, 116, 117, 118, 119, 120, 121];
/** Icy Photosynthesis, and only Icy Photosynthesis, drops the Snowshroom. */
export const SNOWSHROOM_DROP_LEVEL_ID = 117;

/**
 * What a Normal-track win of `levelId` drops, given two uniform [0, 1) rolls
 * (one per item, so on Icy Photosynthesis both can come up together).
 */
export function fungiItemDrops(
  levelId: number,
  shroomRoll: number,
  snowshroomRoll: number,
): FungiItemKind[] {
  const out: FungiItemKind[] = [];
  if (SHROOM_DROP_LEVEL_IDS.includes(levelId) && shroomRoll < FUNGI_ITEM_DROP_CHANCE) {
    out.push('shroom');
  }
  if (levelId === SNOWSHROOM_DROP_LEVEL_ID && snowshroomRoll < FUNGI_ITEM_DROP_CHANCE) {
    out.push('snowshroom');
  }
  return out;
}
